import React, { forwardRef } from 'react';
import { Link } from 'gatsby';
import { Carousel } from '@design-system/carousel';

const SLIDE_KEY = /^s(\d+)\.(.+)$/;

const isImage = (value) =>
  Boolean(value && typeof value === 'object' && typeof value.url === 'string');
const isText = (value) => typeof value === 'string' && value.trim().length > 0;

const parseSlides = (props) => {
  const buckets = new Map();
  let hasPrefixedKeys = false;

  Object.entries(props).forEach(([key, value]) => {
    const match = SLIDE_KEY.exec(key);
    if (!match) return;

    hasPrefixedKeys = true;
    const index = Number(match[1]);
    const field = match[2];

    if (!buckets.has(index)) buckets.set(index, {});
    buckets.get(index)[field] = value;
  });

  if (!hasPrefixedKeys) return [props];

  return [...buckets.entries()].sort(([a], [b]) => a - b).map(([, slide]) => slide);
};

const Slide = ({ image, alt, link }) => {
  if (!isImage(image)) return null;

  const figure = (
    <img
      className="hkx-infografia__image"
      src={image.url}
      alt={isText(alt) ? alt : ''}
      loading="lazy"
    />
  );

  const href = isText(link) ? link.trim() : '';
  if (!href) return figure;

  if (href.startsWith('/')) {
    return (
      <Link className="hkx-infografia__link" to={href}>
        {figure}
      </Link>
    );
  }

  return (
    <a className="hkx-infografia__link" href={href} target="_blank" rel="noopener noreferrer">
      {figure}
    </a>
  );
};

const InfografiaCarousel = forwardRef((props, ref) => {
  const slides = parseSlides(props).filter((slide) => isImage(slide.image));

  if (slides.length === 0) return null;

  if (slides.length === 1) {
    return (
      <section ref={ref} className="hkx-infografia">
        <Slide {...slides[0]} />
      </section>
    );
  }

  return (
    <section ref={ref} className="hkx-infografia hkx-infografia--carousel">
      <Carousel
        className="hkx-infografia-carousel"
        variant="single"
        gap={0}
        centerInsufficientSlides={false}
        buttonPrev={{ 'aria-label': 'Anterior' }}
        buttonNext={{ 'aria-label': 'Siguiente' }}
        ariaLivePlaceholder={(activeIndex, totalCount) => `${activeIndex} de ${totalCount}`}
        items={slides.map((slide) => ({ children: <Slide {...slide} /> }))}
      />
    </section>
  );
});

InfografiaCarousel.displayName = 'InfografiaCarousel';

export default InfografiaCarousel;
